import { FolderOutlined, SearchOutlined } from '@ant-design/icons'
import { useDebounce } from 'ahooks'
import { Avatar, Checkbox, Input, List, Tree } from 'antd'
import { isEqual, isNumber, isString, partition, sortBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Department } from '@/modules/department'
import { DepartmentUser } from '@/modules/project/project-create/project-form.component'
import { User, UserInfo } from '@/modules/user'

import { isIdPureNumber } from './department-tree-transfer.component'

export const getDepartmentChildren = (
  department: DepartmentUser,
  departments: Department[],
  users: User[],
  results: DepartmentUser[]
) => {
  const subDepartmentUsers = users.filter((user) => user.departmentIds[0] === Number(department.id))
  results.push(
    ...[{ id: department.id.toString(), name: department.name, avatar: '' }].concat(
      subDepartmentUsers.map((u) => ({
        id: `${u.departmentIds[0]}-${u.email}`,
        name: u.name,
        avatar: u.avatar,
      }))
    )
  )
  const subDepartments = departments.filter((d) => d.parentId === Number(department.id))
  subDepartments.forEach((d) =>
    getDepartmentChildren({ id: d.id.toString(), name: d.name, avatar: '' }, departments, users, results)
  )
}

export const shouldUncheckParents = (departments: Department[], dataId: string, valueId?: string): boolean => {
  // 仅考虑当前 data 为 department 且 valueId 存在的情形
  if (!isIdPureNumber(dataId) || !valueId) {
    return true
  }
  const valueDepartment = isIdPureNumber(valueId) ? valueId : valueId.split('-')[0]
  const department = departments.find((department) => department.id === Number(valueDepartment))
  return Number(dataId) !== department?.id && shouldUncheckParents(departments, dataId, department?.parentId.toString())
}

export const shouldCheckParents = (
  departmentUsers: DepartmentUser[],
  newDepartmentUser: DepartmentUser,
  users: User[],
  departments: Department[],
  result: DepartmentUser[]
) => {
  const isDepartment = isIdPureNumber(newDepartmentUser.id)
  const department = departments.find(
    (department) => department.id === Number(isDepartment ? newDepartmentUser.id : newDepartmentUser.id.split('-')[0])
  )

  if (department) {
    const currentUsers = departmentUsers
      .filter(
        (departmentUser) =>
          !isIdPureNumber(departmentUser.id) &&
          departmentUser.id.split('-')[0] === (isDepartment ? department.parentId : department.id).toString()
      )
      .map((departmentUser) => departmentUser.id.split('-')[1])
    const currentSubDepartments = departmentUsers
      .filter(
        (departmentUser) =>
          isIdPureNumber(departmentUser.id) && departments.some((d) => d.id.toString() === departmentUser.id)
      )
      .map((departmentUser) => departments.find((d) => d.id.toString() === departmentUser.id)!)
      .filter((d) => d.parentId === (isDepartment ? department.parentId : department.id))
      .map((d) => d.id.toString())

    const expectUsers = users
      .filter((user) => user.departmentIds[0] === (isDepartment ? department.parentId : department.id))
      .map((user) => user.email)
    const expectSubDepartments = departments
      .filter((d) => d.parentId === (isDepartment ? department.parentId : department.id))
      .map((d) => d.id.toString())

    const parentDepartment = departments.find((d) => d.id === (isDepartment ? department.parentId : department.id))

    if (
      isEqual(sortBy(currentUsers), sortBy(expectUsers)) &&
      isEqual(sortBy(currentSubDepartments), sortBy(expectSubDepartments)) &&
      parentDepartment &&
      departmentUsers.every((departmentUser) => departmentUser.id !== parentDepartment.parentId.toString())
    ) {
      const parent = {
        id: parentDepartment.id.toString(),
        name: parentDepartment.name,
        avatar: '',
      }

      result.push(parent)
      shouldCheckParents(
        departmentUsers.concat(result),
        {
          ...parent,
        },
        users,
        departments,
        result
      )
    }
  }
}

export const onDepartmentUserCheck = (
  checked: (string | number)[],
  departments: Department[],
  users: User[],
  setDepartmentUsers: React.Dispatch<React.SetStateAction<DepartmentUser[]>>
) => {
  const keys = checked as (number | string)[]
  const departmentIds = keys.filter((key) => isNumber(key))
  const userIds = keys.filter((key) => isString(key)) as string[]
  const selectedDepartments = departments
    .filter((department) => departmentIds.some((id) => id === department.id))
    .map((department) => ({
      id: department.id.toString(),
      name: department.name,
      avatar: '',
    }))
  const selectedUsers = users
    .filter(
      (user) =>
        userIds.some((id) => id.split('-')?.[1] === user.email) ||
        departmentIds.some((departmentId) => user.departmentIds[0] === departmentId)
    )
    .map((user) => ({
      id: `${user.departmentIds[0]}-${user.email}`,
      name: user.name,
      avatar: user.avatar,
    }))
  setDepartmentUsers(selectedDepartments.concat(selectedUsers))
}

export const DepartmentTreeWithUsers = ({
  users,
  departments,
  userInfo,
  departmentUsers,
  setDepartmentUsers,
}: {
  users: User[]
  departments: Department[]
  userInfo: UserInfo
  defaultPermissions?: string[]
  departmentUsers: DepartmentUser[]
  setDepartmentUsers: React.Dispatch<React.SetStateAction<DepartmentUser[]>>
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 300 })
  const searchInput = (
    <div style={{ padding: '12px 0' }}>
      <Input
        size='small'
        allowClear
        prefix={<SearchOutlined />}
        placeholder={t('search user departments')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
  )
  const searchResult = (
    <List
      dataSource={users
        .filter((user) => user.name.includes(debouncedSearch) || user.email.includes(debouncedSearch))
        .map((user) => ({
          id: `${user.departmentIds[0]}-${user.email}`,
          name: user.name,
          avatar: user.avatar,
        }))
        .concat(
          departments
            .filter((department) => department.name.includes(debouncedSearch))
            .map((department) => ({
              id: department.id.toString(),
              name: department.name,
              avatar: '',
            }))
        )
        .slice(0, 6)}
      renderItem={(value) => {
        let departmentName = ''
        if (!isIdPureNumber(value.id)) {
          departmentName =
            departments.find((department) => department.id.toString() === value.id.split('-')[0])?.name || ''
        }
        return (
          <List.Item className='flex mx-2 items-center justify-between'>
            <div className='flex items-center'>
              <Avatar src={value.avatar} className='flex-shrink-0 mr-10' icon={!value.avatar && <FolderOutlined />} />
              <div className='flex flex-col'>
                <span className='text-xs'>{value.name}</span>
                <span className='text-xs text-[rgba(0,0,0,0.45)]'>{departmentName}</span>
              </div>
            </div>
            <Checkbox
              checked={departmentUsers.some((departmentUser) => departmentUser.id === value.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  if (isIdPureNumber(value.id)) {
                    const departmentUsers = [] as any[]
                    getDepartmentChildren(value, departments, users, departmentUsers)
                    setDepartmentUsers((prev) => {
                      const currentDepartmentUsers = [...prev, ...departmentUsers]
                      const result = [] as DepartmentUser[]
                      shouldCheckParents(currentDepartmentUsers, value, users, departments, result)
                      return currentDepartmentUsers.concat(result)
                    })
                  } else {
                    setDepartmentUsers((prev) => {
                      const currentDepartmentUsers = [
                        ...prev,
                        {
                          ...value,
                        },
                      ]
                      const result = [] as DepartmentUser[]
                      shouldCheckParents(currentDepartmentUsers, value, users, departments, result)

                      return currentDepartmentUsers.concat(result)
                    })
                  }
                } else {
                  setDepartmentUsers((prev) => {
                    let current = prev.filter(
                      (data) =>
                        data.id !== value.id &&
                        !data.id.startsWith(value.id) &&
                        shouldUncheckParents(departments, data.id, value.id)
                    )
                    if (isIdPureNumber(value.id)) {
                      const result = [] as DepartmentUser[]
                      getDepartmentChildren(value, departments, users, result)
                      current = current.filter((c) => result.every((r) => r.id !== c.id))
                    }
                    return current
                  })
                }
              }}
            />
          </List.Item>
        )
      }}
    />
  )

  const defaultExpandedKeys =
    departments
      .find((department) => department.id === userInfo.departmentIds[0])
      ?.path.split('.')
      .map(Number) || []

  const treeData = React.useMemo(() => {
    const roots = departments.filter((department) => department.parentId === 1)
    const combineTreeNode = (root: Department): any => {
      const departmentChildren = departments
        .filter((department) => department.parentId === root.id)
        .sort((a, b) => b.order - a.order)
      const userChildren = users.filter((user) => user.departmentIds[0] === root.id)
      const [leaders, others] = partition(userChildren, (user) => root.leaderWechatIds.includes(user.wechatUserId))
      return {
        title: root.name,
        key: root.id,
        isLeaf: false,
        children: [...leaders, ...others]
          .map((user) => ({
            title: user.name,
            key: `${root.id}-${user.email}`,
            isLeaf: true,
            icon: <Avatar src={user.avatar} size={20} />,
          }))
          .concat(departmentChildren.map((child) => combineTreeNode(child))),
      }
    }
    return roots.map((root) => combineTreeNode(root))
  }, [departments, users])

  return (
    <div>
      {searchInput}
      {search ? (
        searchResult
      ) : (
        <Tree
          autoExpandParent
          multiple
          showIcon
          checkable
          treeData={treeData}
          checkedKeys={departmentUsers.map((departmentUser) =>
            isIdPureNumber(departmentUser.id) ? Number(departmentUser.id) : departmentUser.id
          )}
          defaultExpandedKeys={defaultExpandedKeys}
          onCheck={(checked) =>
            onDepartmentUserCheck(checked as (number | string)[], departments, users, setDepartmentUsers)
          }
        />
      )}
    </div>
  )
}
